import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Search, Camera, Save, AlertCircle, CheckCircle, RotateCcw, Upload } from 'lucide-react';
import { COMERCIO_ID, API_BASE_URL, getApiBaseUrl } from '../config/comercio';
import ClasificacionesDieteticas from './ClasificacionesDieteticas';
import PackagingSelector from './PackagingSelector';
import SimpleToast from './SimpleToast';
import usePopup from '../hooks/usePopup';

const ProductoModal = ({ isOpen, onClose, onProductAdded, productoExistente = null }) => {
  // Hook de toast
  const { toast, showSuccess, showError, showWarning, showInfo, hideToast } = usePopup();
  
  // Estados simples
  const [step, setStep] = useState('scan');
  
  const [codigoBarras, setCodigoBarras] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [scanning, setScanning] = useState(false);

  
  // Estados para clasificaciones dietéticas
  const [clasificacionPrincipal, setClasificacionPrincipal] = useState(null);
  const [deteccionAutomatica, setDeteccionAutomatica] = useState(null);
  
  // Estados para packaging
  const [packagingData, setPackagingData] = useState({
    tipo_packaging_id: null,
    cantidad_packaging: null,
    packaging_completo: null
  });
  
  // Estados para imagen
  const [imagePreview, setImagePreview] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageError, setImageError] = useState('');
  
  // Debug logs
  console.log('🔍 [ProductoModal] Renderizando con:', { isOpen, productoExistente: !!productoExistente, step, imagePreview });
  console.log('📦 [ProductoModal] packagingData actual:', packagingData);
  
  // Formulario simple
  const [formData, setFormData] = useState({
    comercio_id: COMERCIO_ID,
    codigo_personalizado: '',
    nombre: '',
    marca: '',
    precio_compra: '',
    precio_venta: '',
    categoria: 'General',
    stock_actual: '',
    stock_minimo: '',
    descripcion: '',
    imagen_url: ''
  });

  // Referencias
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scannerRef = useRef(null);
  const codigoInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Función para limpiar campo al hacer foco (más confiable que onKeyDown)
  const handleFieldFocus = (fieldName) => {
    console.log(`🔍 [MODAL] Campo ${fieldName} recibió foco, valor actual: ${formData[fieldName]}`);
    
    // Siempre limpiar el campo al hacer foco
    console.log(`🧹 [MODAL] Limpiando campo ${fieldName} al hacer foco`);
    
    // Para todos los campos, usar string vacío
    const defaultValue = '';
    
    // Limpiar inmediatamente
    setFormData(prev => ({
      ...prev,
      [fieldName]: defaultValue
    }));
  };

  // Resetear modal cuando se abre para un producto nuevo
  useEffect(() => {
    if (isOpen && !productoExistente) {
      console.log('🆕 [MODAL] Abriendo para producto nuevo, reseteando estados...');
      setStep('scan');
      setCodigoBarras('');
      setShowCamera(false);
      setFormData({
        codigo_personalizado: '',
        nombre: '',
        marca: '',
        precio_compra: null,
        precio_venta: null,
        categoria: 'General',
        stock_actual: null,
        stock_minimo: null,
        descripcion: '',
        imagen_url: '',
        clasificaciones_dieteticas: [],
        clasificacion_principal_id: null,
        fuente_clasificacion: 'Manual',
        tipo_packaging_id: null,
        cantidad_packaging: '',
        packaging_completo: false
      });
      setClasificacionPrincipal(null);
      setImageError('');
      setImagePreview('');
      setSelectedFile(null);
    }
  }, [isOpen, productoExistente]);
  
  // Debug: Log del estado de clasificacionPrincipal
  useEffect(() => {
    console.log('🔄 [DEBUG] clasificacionPrincipal cambió:', clasificacionPrincipal);
    console.log('🔄 [DEBUG] Tipo de clasificacionPrincipal:', typeof clasificacionPrincipal);
    console.log('🔄 [DEBUG] Valor exacto:', JSON.stringify(clasificacionPrincipal));
  }, [clasificacionPrincipal]);

  // Debug: Log del estado de imagePreview
  useEffect(() => {
    console.log('🖼️ [DEBUG] imagePreview cambió:', imagePreview);
    console.log('🖼️ [DEBUG] Tipo de imagePreview:', typeof imagePreview);
    console.log('🖼️ [DEBUG] Longitud de imagePreview:', imagePreview ? imagePreview.length : 0);
  }, [imagePreview]);

  // Debug: Log del estado de packagingData
  useEffect(() => {
    console.log('📦 [DEBUG] packagingData cambió:', packagingData);
    console.log('📦 [DEBUG] Tipo de packagingData:', typeof packagingData);
    console.log('📦 [DEBUG] tipo_packaging_id:', packagingData.tipo_packaging_id);
    console.log('📦 [DEBUG] cantidad_packaging:', packagingData.cantidad_packaging);
    console.log('📦 [DEBUG] packaging_completo:', packagingData.packaging_completo);
  }, [packagingData]);

  // Cargar producto existente cuando se abra el modal
  useEffect(() => {
    if (isOpen && productoExistente) {
      console.log('📦 [MODAL] Cargando producto existente:', productoExistente);
      console.log('🔍 [MODAL] Clasificación principal ID:', productoExistente.clasificacion_principal_id);
      console.log('🔍 [MODAL] Clasificación principal nombre:', productoExistente.clasificacion_especial_nombre);
      console.log('🔍 [MODAL] Fuente clasificación:', productoExistente.fuente_clasificacion);
      console.log('🔍 [MODAL] Todos los campos del producto:', Object.keys(productoExistente));
      console.log('📦 [MODAL] Packaging del producto existente:', {
        tipo_packaging_id: productoExistente.tipo_packaging_id,
        cantidad_packaging: productoExistente.cantidad_packaging,
        packaging_completo: productoExistente.packaging_completo
      });
      console.log('📦 [MODAL] TODOS los campos del producto existente:', productoExistente);
      
      // Cargar datos básicos del formulario mientras se buscan los completos
      setFormData({
        comercio_id: COMERCIO_ID,
        codigo_personalizado: productoExistente.codigo_personalizado || '',
        nombre: productoExistente.nombre || '',
        marca: productoExistente.marca || '',
        precio_compra: parseFloat(productoExistente.precio_compra) || 0.00,
        precio_venta: parseFloat(productoExistente.precio_venta) || 0.00,
        categoria: productoExistente.categoria || 'General',
        stock_actual: parseInt(productoExistente.stock_actual) || 0,
        stock_minimo: parseInt(productoExistente.stock_minimo) || 0,
        descripcion: productoExistente.descripcion || '',
        imagen_url: productoExistente.imagen_url || ''
      });
      
      // Mostrar imagen si existe
      if (productoExistente.imagen_url) {
        console.log('🖼️ [MODAL] Cargando imagen del producto existente:', productoExistente.imagen_url);
        setImagePreview(productoExistente.imagen_url);
        setSelectedFile(null); // No hay archivo local, viene de la base de datos
        // Asegurar que formData también tenga la imagen_url
        setFormData(prev => ({ ...prev, imagen_url: productoExistente.imagen_url }));
        console.log('🖼️ [MODAL] Imagen cargada y formData actualizado:', productoExistente.imagen_url);
      } else {
        console.log('⚠️ [MODAL] No hay imagen en el producto existente');
        setImagePreview('');
        setSelectedFile(null);
        setFormData(prev => ({ ...prev, imagen_url: '' }));
      }
      
      // Cargar clasificación dietética si existe
      if (productoExistente.clasificacion_principal_id) {
        const clasificacionId = parseInt(productoExistente.clasificacion_principal_id);
        console.log('🌟 [MODAL] Estableciendo clasificación principal:', clasificacionId);
        setClasificacionPrincipal(clasificacionId);
        console.log('🌟 [MODAL] Clasificación cargada desde producto existente:', clasificacionId);
        console.log('🌟 [MODAL] Tipo de clasificación:', typeof clasificacionId);
      } else {
        console.log('⚠️ [MODAL] No hay clasificación principal ID en el producto');
        console.log('⚠️ [MODAL] Campos disponibles:', Object.keys(productoExistente).filter(key => key.includes('clasificacion')));
      }
      
      // Cargar fuente de clasificación si existe
      if (productoExistente.fuente_clasificacion) {
        setDeteccionAutomatica({
          fuente: productoExistente.fuente_clasificacion,
          confianza: 'existente'
        });
        console.log('🔍 [MODAL] Fuente de clasificación cargada:', productoExistente.fuente_clasificacion);
      }
      
      // Cargar datos de packaging iniciales
      const packagingInicial = {
        tipo_packaging_id: productoExistente.tipo_packaging_id || null,
        cantidad_packaging: productoExistente.cantidad_packaging || null,
        packaging_completo: productoExistente.packaging_completo || null
      };
      
      console.log('📦 [MODAL] Packaging inicial cargado:', packagingInicial);
      console.log('📦 [MODAL] Estableciendo packagingData con setPackagingData...');
      
      setPackagingData(packagingInicial);
      
      // Verificar que se estableció correctamente
      setTimeout(() => {
        console.log('📦 [MODAL] Verificando packagingData después de setState:', packagingInicial);
      }, 100);
      
      // Ir directamente al paso de edición
      console.log('🔄 [MODAL] Estableciendo step a "form" para edición');
      console.log('🚨 [MODAL] CAMBIO FORZADO - DEBE APARECER EN CONSOLA');
      setStep('form');
      setCodigoBarras(productoExistente.codigo_personalizado || '');
      
      // Verificar que se estableció correctamente
      setTimeout(() => {
        console.log('🔄 [MODAL] Verificando step después de setState:', step);
        if (step !== 'form') {
          console.log('⚠️ [MODAL] Step no es "form", forzando cambio...');
          setStep('form');
        }
      }, 100);
      
      // BUSCAR DATOS COMPLETOS del producto desde la base de datos
      buscarProductoCompleto(productoExistente.codigo_personalizado);
      
      console.log('✅ [MODAL] Producto existente cargado correctamente');
    }
  }, [isOpen, productoExistente]);
  
  // Resetear modal SOLO cuando se cierre completamente
  useEffect(() => {
    console.log('🔍 [MODAL] useEffect isOpen cambió:', isOpen);
    if (!isOpen) {
      // Solo resetear cuando se cierre, NO cuando se abra
      console.log('🔒 [MODAL] Cerrando modal, reseteando estados...');
      setStep('scan');
      setCodigoBarras('');
      setShowCamera(false);
      setScanning(false);
      setClasificacionPrincipal(null);
      setDeteccionAutomatica(null);
      setPackagingData({
        tipo_packaging_id: null,
        cantidad_packaging: null,
        packaging_completo: null
      });
      setFormData({
        comercio_id: COMERCIO_ID,
        codigo_personalizado: '',
        nombre: '',
        marca: '',
          precio_compra: null,
          precio_venta: null,
          categoria: 'General',
          stock_actual: null,
          stock_minimo: null,
        descripcion: '',
        imagen_url: ''
      });
    } else {
      // Cuando se abre el modal, enfocar el campo código después de un pequeño delay
      setTimeout(() => {
        if (codigoInputRef.current) {
          codigoInputRef.current.focus();
          console.log('🎯 [MODAL] Campo código enfocado automáticamente');
        }
      }, 100);
    }
  }, [isOpen]);

  // Monitorear cambios en step para debug
  useEffect(() => {
    console.log('🔄 [STEP] step cambió a:', step);
    console.log('🔄 [STEP] isOpen:', isOpen);
  }, [step, isOpen]);

  // Función para manejar cambios de packaging
  const handlePackagingChange = useCallback((newPackagingData) => {
    setPackagingData(newPackagingData);
    console.log('📦 [PACKAGING] Datos actualizados:', newPackagingData);
  }, []);

  // Función helper para obtener nombre del tipo de packaging
  const getNombreTipoPackaging = (tipoId) => {
    const tipos = {
      1: 'Unidad',
      2: 'Kilo',
      3: 'Gramos',
      4: 'Litro',
      5: 'Caja',
      6: 'Docena',
      7: 'Metro',
      8: 'Bolsa'
    };
    return tipos[tipoId] || '';
  };

  // Función para detectar clasificación dietética principal automáticamente
  const detectarClasificacionPrincipal = (producto) => {
    console.log('🌱 Detectando clasificación dietética principal para:', producto.nombre);
    
    let clasificacionPrincipal = null;
    
    // Detectar por nombre del producto
    const nombreLower = producto.nombre?.toLowerCase() || '';
    const descripcionLower = producto.descripcion?.toLowerCase() || '';
    const ingredientesLower = producto.ingredientes?.toLowerCase() || '';
    const categoriaLower = producto.categoria?.toLowerCase() || '';
    
    // Detectar por etiquetas de Open Food Facts
    const labelsTags = producto.labels_tags || [];
    const labelsLower = labelsTags.map(label => label.toLowerCase());
    
    // Detectar por categorías
    const categoriesTags = producto.categories_tags || [];
    const categoriesLower = categoriesTags.map(cat => cat.toLowerCase());
    
    // 🌟 PRIORIDAD 1: CLASIFICACIONES ESPECIALES (MÁS IMPORTANTES)
    
    // Detectar Vegano
    if (nombreLower.includes('vegano') || 
        labelsLower.includes('vegan') || 
        labelsLower.includes('vegano') ||
        ingredientesLower.includes('sin productos animales') ||
        ingredientesLower.includes('sin leche') ||
        ingredientesLower.includes('sin huevos')) {
      clasificacionPrincipal = 'Vegano';
    }
    
    // Detectar Vegetariano
    if (!clasificacionPrincipal && (nombreLower.includes('vegetariano') || 
        labelsLower.includes('vegetarian') ||
        (ingredientesLower.includes('sin carne') && !ingredientesLower.includes('vegano')))) {
      clasificacionPrincipal = 'Vegetariano';
    }
    
    // Detectar Sin Gluten/Sin TACC
    if (!clasificacionPrincipal && (nombreLower.includes('sin gluten') || 
        nombreLower.includes('sin tacc') ||
        nombreLower.includes('gluten free') ||
        labelsLower.includes('gluten-free') || 
        labelsLower.includes('sin-gluten') ||
        ingredientesLower.includes('sin gluten') ||
        ingredientesLower.includes('sin tacc'))) {
      clasificacionPrincipal = 'Sin Gluten/Sin TACC';
    }
    
    // Detectar Ayurvédica
    if (!clasificacionPrincipal && (nombreLower.includes('ayurvédica') || 
        nombreLower.includes('ayurvedica') ||
        nombreLower.includes('ayurveda') ||
        labelsLower.includes('ayurvedic') ||
        categoriaLower.includes('medicina natural') ||
        categoriaLower.includes('suplementos naturales'))) {
      clasificacionPrincipal = 'Ayurvédica';
    }
    
    // Detectar Sin Lactosa
    if (!clasificacionPrincipal && (nombreLower.includes('sin lactosa') || 
        labelsLower.includes('lactose-free') || 
        labelsLower.includes('sin-lactosa') ||
        ingredientesLower.includes('sin lactosa') ||
        ingredientesLower.includes('lactosa free'))) {
      clasificacionPrincipal = 'Sin Lactosa';
    }
    
    // 🌱 PRIORIDAD 2: CLASIFICACIONES POR CATEGORÍAS
    
    // Detectar por categorías principales
    if (!clasificacionPrincipal && (categoriaLower.includes('frutas') || 
        categoriaLower.includes('verduras') || 
        categoriaLower.includes('vegetales') ||
        categoriesLower.some(cat => cat.includes('fruits') || cat.includes('vegetables')))) {
      clasificacionPrincipal = 'Frutas y Verduras';
    }
    
    if (!clasificacionPrincipal && (categoriaLower.includes('lácteos') || 
        categoriaLower.includes('lacteos') || 
        categoriaLower.includes('leche') ||
        categoriesLower.some(cat => cat.includes('dairy') || cat.includes('milk')))) {
      clasificacionPrincipal = 'Lácteos';
    }
    
    if (!clasificacionPrincipal && (categoriaLower.includes('carnes') || 
        categoriaLower.includes('pollo') || 
        categoriaLower.includes('pescado') ||
        categoriesLower.some(cat => cat.includes('meat') || cat.includes('fish') || cat.includes('poultry')))) {
      clasificacionPrincipal = 'Carnes';
    }
    
    if (!clasificacionPrincipal && (categoriaLower.includes('cereales') || 
        categoriaLower.includes('granos') || 
        categoriaLower.includes('arroz') ||
        categoriesLower.some(cat => cat.includes('grains') || cat.includes('cereals')))) {
      clasificacionPrincipal = 'Cereales y Granos';
    }
    
    if (!clasificacionPrincipal && (categoriaLower.includes('bebidas') || 
        categoriaLower.includes('agua') || 
        categoriaLower.includes('jugo') ||
        categoriesLower.some(cat => cat.includes('beverages') || cat.includes('drinks')))) {
      clasificacionPrincipal = 'Bebidas';
    }
    
    // 🌱 PRIORIDAD 3: CLASIFICACIONES ORGÁNICAS/NATURALES
    
    if (!clasificacionPrincipal && (nombreLower.includes('orgánico') || 
        nombreLower.includes('organico') || 
        labelsLower.includes('organic') || 
        labelsLower.includes('organico'))) {
      clasificacionPrincipal = 'Orgánico';
    }
    
    if (!clasificacionPrincipal && (nombreLower.includes('natural') || 
        labelsLower.includes('natural') ||
        ingredientesLower.includes('ingredientes naturales'))) {
      clasificacionPrincipal = 'Natural';
    }
    
    // 📦 CLASIFICACIÓN POR DEFECTO
    if (!clasificacionPrincipal) {
      clasificacionPrincipal = 'Producto General';
    }
    
    console.log('🌟 Clasificación principal detectada:', clasificacionPrincipal);
    
    return {
      clasificacionPrincipal,
      fuente: 'API_Automatica',
      confianza: clasificacionPrincipal !== 'Producto General' ? 'alta' : 'baja'
    };
  };

    // Función para buscar producto en comercio local
  const buscarEnComercio = async (codigo) => {
    try {
      console.log('🔍 Buscando en comercio local:', codigo);
      const response = await fetch(`${API_BASE_URL}/productos_comercio_buscar.php?codigo=${codigo}&comercio_id=${COMERCIO_ID}`);
      const data = await response.json();
      
             if (data.success && data.producto) {
          console.log('✅ Producto encontrado en comercio:', data.producto);
          return data.producto;
        }
       return null;
     } catch (err) {
       console.log('❌ Error buscando en comercio:', err);
       return null;
     }
   };

  // Función para buscar producto completo desde la base de datos
  const buscarProductoCompleto = async (codigo) => {
    try {
      console.log('🔍 [COMPLETO] Buscando producto completo:', codigo);
      const productoCompleto = await buscarEnComercio(codigo);
      
      if (productoCompleto) {
        console.log('✅ [COMPLETO] Producto completo encontrado:', productoCompleto);
        
        // Cargar datos de packaging completos solo si no tenemos datos previos
        if (!packagingData.tipo_packaging_id && productoCompleto.tipo_packaging_id) {
          console.log('📦 [COMPLETO] Cargando packaging desde datos completos:', {
            tipo_packaging_id: productoCompleto.tipo_packaging_id,
            cantidad_packaging: productoCompleto.cantidad_packaging,
            packaging_completo: productoCompleto.packaging_completo
          });
          setPackagingData({
            tipo_packaging_id: productoCompleto.tipo_packaging_id || null,
            cantidad_packaging: productoCompleto.cantidad_packaging || null,
            packaging_completo: productoCompleto.packaging_completo || null
          });
        } else {
          console.log('📦 [COMPLETO] Manteniendo packaging existente:', packagingData);
        }
        
        // Cargar clasificación principal completa
        if (productoCompleto.clasificacion_principal_id) {
          setClasificacionPrincipal(parseInt(productoCompleto.clasificacion_principal_id));
        }
        
        // Cargar información de detección automática completa
        if (productoCompleto.fuente_clasificacion) {
          setDeteccionAutomatica({
            fuente: productoCompleto.fuente_clasificacion,
            confianza: 'existente'
          });
        }
        
        // Cargar imagen si existe
        if (productoCompleto.imagen_url) {
          setImagePreview(productoCompleto.imagen_url);
          setSelectedFile(null); // No hay archivo local, viene de la base de datos
          console.log('🖼️ [COMPLETO] Imagen cargada desde datos completos:', productoCompleto.imagen_url);
        }
        
        console.log('✅ [COMPLETO] Datos completos cargados correctamente');
      } else {
        console.log('⚠️ [COMPLETO] No se pudo obtener datos completos del producto');
      }
    } catch (err) {
      console.error('❌ [COMPLETO] Error buscando producto completo:', err);
    }
  };

  // Función simple para buscar producto
  const buscarProducto = async (codigo = null) => {
    const codigoABuscar = codigo || codigoBarras;
    
    if (!codigoABuscar.trim()) {
      showError('Ingresa un código de barras', 'Código de Barras Requerido');
      return;
    }

    setLoading(true);
    // setError eliminado - ahora se usan popups
    // setSuccess eliminado - ahora se usan popups

    try {
      console.log('🔍 Buscando producto:', codigoABuscar);
      
      // PRIMERO: Buscar en productos_ar (tabla universal)
      let productoEncontrado = null;
      let fuenteAPI = '';
      
      try {
        console.log('🇦🇷 Buscando en productos_ar (tabla universal)...');
        const response = await fetch(`${API_BASE_URL}/productos_ar_buscar.php?codigo=${codigoABuscar}`);
        const data = await response.json();
        
        if (data.success && data.producto) {
          console.log('✅ Producto encontrado en productos_ar:', data.producto);
          productoEncontrado = data.producto;
          fuenteAPI = 'Productos Argentinos (Universal)';
        }
      } catch (err) {
        console.log('⚠️ Error buscando en productos_ar:', err);
      }
      
      // SEGUNDO: Buscar en comercio local
      const productoLocal = await buscarEnComercio(codigoABuscar);
      
      if (productoLocal) {
        console.log('✅ Producto encontrado en comercio local:', productoLocal);
        
        // Cargar datos del comercio local (datos específicos del comercio)
        setFormData({
          comercio_id: COMERCIO_ID,
          codigo_personalizado: productoLocal.codigo_personalizado || codigoABuscar,
          nombre: productoLocal.nombre || '',
          marca: productoLocal.marca || '',
          precio_compra: parseFloat(productoLocal.precio_compra) || '',
          precio_venta: parseFloat(productoLocal.precio_venta) || '',
          categoria: productoLocal.categoria || 'General',
          stock_actual: parseInt(productoLocal.stock_actual) || '',
          stock_minimo: parseInt(productoLocal.stock_minimo) || '',
          descripcion: productoLocal.descripcion || '',
          imagen_url: productoLocal.imagen_url || ''
        });
        
        // Cargar datos de packaging
        setPackagingData({
          tipo_packaging_id: productoLocal.tipo_packaging_id || null,
          cantidad_packaging: productoLocal.cantidad_packaging || null,
          packaging_completo: productoLocal.packaging_completo || null
        });
        
        // Cargar clasificación dietética si existe
        if (productoLocal.clasificacion_principal_id) {
          console.log('🌟 [COMERCIO] Cargando clasificación principal:', productoLocal.clasificacion_principal_id);
          setClasificacionPrincipal(productoLocal.clasificacion_principal_id);
        }
        
        // Mostrar imagen si existe
        if (productoLocal.imagen_url) {
          console.log('🖼️ [COMERCIO] Imagen encontrada en comercio:', productoLocal.imagen_url);
          console.log('🖼️ [COMERCIO] Estableciendo imagePreview:', productoLocal.imagen_url);
          setImagePreview(productoLocal.imagen_url);
          setSelectedFile(null);
          console.log('🖼️ [COMERCIO] imagePreview establecido, verificando en 100ms...');
          setTimeout(() => {
            console.log('🖼️ [COMERCIO] imagePreview actual:', imagePreview);
          }, 100);
        } else {
          console.log('⚠️ [COMERCIO] No hay imagen en comercio');
          setImagePreview('');
          setSelectedFile(null);
        }
        
        showSuccess(`✅ Producto encontrado en tu comercio! Puedes modificar los datos.`, 'Producto Existente');
        setStep('form');
        return; // Salir aquí para evitar más llamadas a la API
      }
      
      if (productoEncontrado) {
        console.log('✅ Producto encontrado en productos_ar (universal):', productoEncontrado);
        
        // Cargar datos de productos_ar (datos fijos del fabricante)
        setFormData({
          comercio_id: COMERCIO_ID,
          codigo_personalizado: productoEncontrado.codigo_barras || codigoABuscar,
          nombre: productoEncontrado.nombre || '',
          marca: productoEncontrado.marca || '',
          precio_compra: parseFloat(productoEncontrado.precio_compra) || '',
          precio_venta: parseFloat(productoEncontrado.precio_venta) || '',
          categoria: productoEncontrado.categoria || 'General',
          stock_actual: parseInt(productoEncontrado.stock_disponible) || '',
          stock_minimo: parseInt(productoEncontrado.stock_minimo) || '',
          descripcion: productoEncontrado.descripcion || '',
          imagen_url: productoEncontrado.imagen_url || ''
        });
        
        // Mostrar imagen si existe
        if (productoEncontrado.imagen_url) {
          console.log('🖼️ [API] Imagen encontrada en productos_ar:', productoEncontrado.imagen_url);
          console.log('🖼️ [API] Estableciendo imagePreview:', productoEncontrado.imagen_url);
          setImagePreview(productoEncontrado.imagen_url);
          setSelectedFile(null); // No hay archivo local, viene de la API
          console.log('🖼️ [API] imagePreview establecido, verificando en 100ms...');
          setTimeout(() => {
            console.log('🖼️ [API] imagePreview actual:', imagePreview);
          }, 100);
        } else {
          console.log('⚠️ [API] No hay imagen en productos_ar');
          setImagePreview('');
          setSelectedFile(null);
        }
        
        console.log('🔍 [DEBUG] Antes de showSuccess - step:', step);
        showSuccess(`✅ Producto encontrado en ${fuenteAPI}! Completa los datos para tu comercio.`, 'Producto Encontrado');
        console.log('🔍 [DEBUG] Después de showSuccess - step:', step);
        
        console.log('🔍 [DEBUG] Antes de setStep("form"), step actual:', step);
        setStep('form');
        console.log('🔍 [DEBUG] setStep("form") ejecutado, step actual:', step);
        
        // Forzar un re-render para asegurar que el step cambie
        setTimeout(() => {
          console.log('🔍 [DEBUG] Después de setTimeout, step actual:', step);
          console.log('🔍 [DEBUG] Verificando si el step cambió correctamente...');
        }, 100);
        
        // NO intentar crear en productos_ar porque ya existe
        console.log('ℹ️ [INFO] Producto ya existe en productos_ar, no se intentará crear de nuevo');
        
        // IMPORTANTE: Salir de la función aquí para no continuar con APIs externas
        console.log('🔍 [DEBUG] Saliendo de la función con return');
        return;
        
      } else if (productoLocal) {
        console.log('✅ Producto encontrado en comercio local:', productoLocal);
        
        // Cargar datos existentes para edición
        setFormData({
          comercio_id: COMERCIO_ID,
          codigo_personalizado: productoLocal.codigo_personalizado || codigoABuscar,
          nombre: productoLocal.nombre || '',
          marca: productoLocal.marca || '',
          precio_compra: parseFloat(productoLocal.precio_compra) || '',
          precio_venta: parseFloat(productoLocal.precio_venta) || '',
          categoria: productoLocal.categoria || 'General',
          stock_actual: parseInt(productoLocal.stock_actual) || '',
          stock_minimo: parseInt(productoLocal.stock_minimo) || '',
          descripcion: productoLocal.descripcion || '',
          imagen_url: productoLocal.imagen_url || ''
        });
        
        // Mostrar imagen si existe
        if (productoLocal.imagen_url) {
          console.log('🖼️ [LOCAL] Imagen encontrada en productos_comercio:', productoLocal.imagen_url);
          setImagePreview(productoLocal.imagen_url);
          setSelectedFile(null); // No hay archivo local, viene de la base de datos
        } else {
          console.log('⚠️ [LOCAL] No hay imagen en productos_comercio');
        }
        
        // Cargar clasificación principal existente
        if (productoLocal.clasificacion_principal_id) {
          console.log('🌟 Clasificación principal encontrada:', productoLocal.clasificacion_principal_id);
          console.log('🌟 Tipo de dato:', typeof productoLocal.clasificacion_principal_id);
          console.log('🌟 Estructura completa del objeto:', JSON.stringify(productoLocal.clasificacion_principal_id, null, 2));
          
          // Manejar diferentes tipos de datos
          let clasificacionId = null;
          
          if (typeof productoLocal.clasificacion_principal_id === 'number') {
            clasificacionId = productoLocal.clasificacion_principal_id;
          } else if (typeof productoLocal.clasificacion_principal_id === 'string') {
            clasificacionId = parseInt(productoLocal.clasificacion_principal_id);
          } else if (typeof productoLocal.clasificacion_principal_id === 'object' && productoLocal.clasificacion_principal_id !== null) {
            // Si es un objeto, intentar extraer el ID
            if (productoLocal.clasificacion_principal_id.id) {
              clasificacionId = parseInt(productoLocal.clasificacion_principal_id.id);
            } else if (productoLocal.clasificacion_principal_id.clasificacion_principal_id) {
              clasificacionId = parseInt(productoLocal.clasificacion_principal_id.clasificacion_principal_id);
            }
          }
          
          console.log('🌟 ID extraído:', clasificacionId);
          
          if (clasificacionId && !isNaN(clasificacionId)) {
            setClasificacionPrincipal(clasificacionId);
            console.log('✅ Clasificación principal establecida correctamente:', clasificacionId);
          } else {
            console.log('⚠️ No se pudo extraer un ID válido');
            setClasificacionPrincipal(null);
          }
        } else {
          console.log('⚠️ No se encontró clasificación principal');
          setClasificacionPrincipal(null);
        }
        

        
        // Cargar información de detección automática
        if (productoLocal.fuente_clasificacion) {
          setDeteccionAutomatica({
            fuente: productoLocal.fuente_clasificacion,
            confianza: 'existente'
          });
        }
        
        // Cargar datos de packaging existentes
        console.log('📦 [PACKAGING] Cargando datos existentes:', {
          tipo_packaging_id: productoLocal.tipo_packaging_id,
          cantidad_packaging: productoLocal.cantidad_packaging,
          packaging_completo: productoLocal.packaging_completo
        });
        
        const packagingDataToSet = {
          tipo_packaging_id: productoLocal.tipo_packaging_id || null,
          cantidad_packaging: productoLocal.cantidad_packaging || null,
          packaging_completo: productoLocal.packaging_completo || null
        };
        
        console.log('📦 [PACKAGING] Datos a establecer:', packagingDataToSet);
        
        setPackagingData(packagingDataToSet);
        
        console.log('📦 [PACKAGING] Estados establecidos, verificando...');
        
        // Verificar que los datos se establecieron correctamente
        setTimeout(() => {
          console.log('📦 [PACKAGING] Estado actual después de setState:', packagingData);
          console.log('📦 [PACKAGING] Verificando que formData también se estableció:', formData);
        }, 100);
        
        // Verificar también que formData se estableció correctamente
        console.log('📋 [FORM] Datos del formulario establecidos:', {
          nombre: productoLocal.nombre,
          marca: productoLocal.marca,
          stock_actual: productoLocal.stock_actual,
          precio_compra: productoLocal.precio_compra,
          precio_venta: productoLocal.precio_venta
        });
        
        // Verificar también que formData se estableció correctamente
        console.log('📋 [FORM] Datos del formulario establecidos:', {
          nombre: productoLocal.nombre,
          marca: productoLocal.marca,
          stock_actual: productoLocal.stock_actual,
          precio_compra: productoLocal.precio_compra,
          precio_venta: productoLocal.precio_venta
        });
        
        showSuccess('✅ Producto encontrado en tu comercio. Puedes editarlo y actualizarlo.', 'Producto Encontrado');
        setStep('edit');
        return;
      }
      
       // PASO 2: No intentar crear en productos_ar porque ya existe
       console.log('ℹ️ [INFO] Producto ya existe en productos_ar, saltando creación');
       
       // TERCERO: Ahora buscar en múltiples APIs para obtener información completa
       console.log('🌍 Buscando en múltiples APIs...');
       
       // API 1: Open Food Facts (Internacional)
       try {
         console.log('🌍 API 1: Open Food Facts...');
         const response = await fetch(`https://world.openfoodfacts.org/api/v0/product/${codigoABuscar}.json`);
         const data = await response.json();
         
         if (data.status === 1 && data.product) {
           console.log('✅ Producto encontrado en Open Food Facts:', data.product);
           productoEncontrado = data.product;
           fuenteAPI = 'Open Food Facts (Internacional)';
         }
       } catch (err) {
         console.log('⚠️ Error en Open Food Facts:', err);
       }
       
       // API 2: Productos Argentinos ya se buscó al inicio
       
       // API 3: Mercado Libre (Productos argentinos)
       if (!productoEncontrado) {
         try {
           console.log('🛒 API 3: Mercado Libre...');
           const response = await fetch(`https://api.mercadolibre.com/sites/MLA/search?q=${codigoABuscar}&limit=1`);
           const data = await response.json();
           
           if (data.results && data.results.length > 0) {
             const productoML = data.results[0];
             console.log('✅ Producto encontrado en Mercado Libre:', productoML);
             
             // Convertir formato de Mercado Libre a nuestro formato
             productoEncontrado = {
               product_name: productoML.title,
               brands: productoML.seller?.eshop?.nick_name || 'Mercado Libre',
               categories_tags: [productoML.category_id],
               ingredients_text: productoML.attributes?.find(attr => attr.id === 'INGREDIENTS')?.value_name || '',
               image_url: productoML.thumbnail,
               origins: 'Argentina',
               labels_tags: ['Mercado Libre']
             };
             fuenteAPI = 'Mercado Libre (Argentina)';
           }
         } catch (err) {
           console.log('⚠️ Error en Mercado Libre:', err);
         }
       }
       
       // API 4: Precios Claros (Gobierno argentino)
       if (!productoEncontrado) {
         try {
           console.log('🏛️ API 4: Precios Claros...');
           const response = await fetch(`https://preciosclaros.gob.ar/api/v1/productos?codigo=${codigoABuscar}`);
           const data = await response.json();
           
           if (data.productos && data.productos.length > 0) {
             const productoPC = data.productos[0];
             console.log('✅ Producto encontrado en Precios Claros:', productoPC);
             
             // Convertir formato de Precios Claros a nuestro formato
             productoEncontrado = {
               product_name: productoPC.nombre,
               brands: productoPC.marca || 'Sin marca',
               categories_tags: [productoPC.categoria || 'General'],
               ingredients_text: productoPC.descripcion || '',
               image_url: '',
               origins: 'Argentina',
               labels_tags: ['Precios Claros']
             };
             fuenteAPI = 'Precios Claros (Gobierno)';
           }
         } catch (err) {
           console.log('⚠️ Error en Precios Claros:', err);
         }
       }
       
                // Si se encontró el producto en alguna API
         if (productoEncontrado) {
           console.log(`✅ Producto encontrado en: ${fuenteAPI}`);
           
                       // DETECTAR CLASIFICACIÓN DIETÉTICA PRINCIPAL AUTOMÁTICAMENTE
            const clasificacionDetectada = detectarClasificacionPrincipal(productoEncontrado);
            setDeteccionAutomatica(clasificacionDetectada);
            
            // Buscar el ID real de la clasificación principal en la base de datos
            if (clasificacionDetectada.clasificacionPrincipal) {
              try {
                const response = await fetch(`${getApiBaseUrl()}/clasificaciones_dieteticas.php`);
                const data = await response.json();
                
                if (data.success) {
                  const clasificacionesDB = data.clasificaciones;
                  
                  // Buscar ID de la clasificación principal
                  const clasificacionPrincipalDB = clasificacionesDB.find(c => 
                    c.nombre.toLowerCase() === clasificacionDetectada.clasificacionPrincipal.toLowerCase()
                  );
                  
                  if (clasificacionPrincipalDB) {
                    setClasificacionPrincipal(clasificacionPrincipalDB.id);
                    console.log('🌟 Clasificación principal detectada y mapeada:', clasificacionPrincipalDB.id);
                  }
                }
              } catch (err) {
                console.log('⚠️ Error al mapear clasificación principal:', err);
              }
            }
           
           // Cargar TODOS los campos disponibles de la API
           setFormData({
             comercio_id: COMERCIO_ID,
             codigo_personalizado: codigoABuscar,
             nombre: productoEncontrado.product_name || productoEncontrado.nombre || 'Producto sin nombre',
             marca: productoEncontrado.brands || productoEncontrado.marca || 'Sin marca',
             precio_compra: 0.00, // Se debe completar manualmente
             precio_venta: 0.00, // Se debe completar manualmente
             categoria: productoEncontrado.categories_tags?.[0]?.replace('en:', '') || productoEncontrado.marca || 'General',
             stock_actual: 0, // Se debe completar manualmente
             stock_minimo: 0, // Se debe completar manualmente
             descripcion: productoEncontrado.ingredients_text || productoEncontrado.descripcion || 'Sin descripción',
             imagen_url: productoEncontrado.image_url || ''
           });
         
        // PASO 2: Crear producto en productos_ar con datos de la API
        console.log('💾 Creando producto en productos_ar con datos de la API...');
        
         try {
          const productoArNuevo = {
             codigo_barras: codigoABuscar,
            nombre: productoEncontrado.nombre || productoEncontrado.product_name || 'Producto encontrado',
            marca: productoEncontrado.marca || productoEncontrado.brands || 'Sin marca',
            categoria: productoEncontrado.categoria || productoEncontrado.categories_tags?.[0] || 'General',
            descripcion: productoEncontrado.descripcion || productoEncontrado.ingredients_text || 'Producto encontrado en API',
            imagen_url: productoEncontrado.imagen_url || productoEncontrado.image_url || '',
            precio: 0.00,
            precio_compra: 0.00,
            stock_disponible: 0,
            unidad_medida: 'unidad',
            es_argentino: true
           };
           
          const response = await fetch(`${API_BASE_URL}/productos_ar_crear.php`, {
             method: 'POST',
             headers: { 
               'Content-Type': 'application/json',
               'Accept': 'application/json'
             },
            body: JSON.stringify(productoArNuevo)
           });
           
           const resultado = await response.json();
           if (resultado.success) {
            console.log('✅ Producto creado en productos_ar con información de la API');
            
            // Cargar datos del producto creado en el formulario
            setFormData({
              comercio_id: COMERCIO_ID,
              codigo_personalizado: codigoABuscar,
              nombre: productoEncontrado.nombre || productoEncontrado.product_name || 'Producto encontrado',
              marca: productoEncontrado.marca || productoEncontrado.brands || 'Sin marca',
              precio_compra: '',
              precio_venta: '',
              categoria: productoEncontrado.categoria || productoEncontrado.categories_tags?.[0] || 'General',
              stock_actual: '',
              stock_minimo: '',
              descripcion: productoEncontrado.descripcion || productoEncontrado.ingredients_text || 'Producto encontrado en API',
              imagen_url: productoEncontrado.imagen_url || productoEncontrado.image_url || ''
            });
            
            // Cargar imagen si existe
            const imagenUrl = productoEncontrado.imagen_url || productoEncontrado.image_url;
            if (imagenUrl) {
              console.log('🖼️ [API] Imagen encontrada en API externa:', imagenUrl);
              console.log('🖼️ [API] Estableciendo imagePreview:', imagenUrl);
              setImagePreview(imagenUrl);
              setSelectedFile(null);
              console.log('🖼️ [API] imagePreview establecido, verificando en 100ms...');
              setTimeout(() => {
                console.log('🖼️ [API] imagePreview actual:', imagePreview);
              }, 100);
            } else {
              console.log('⚠️ [API] No hay imagen en API externa');
              setImagePreview('');
              setSelectedFile(null);
            }
            
           } else {
            console.log('⚠️ Error al crear producto en productos_ar:', resultado.message);
           }
         } catch (err) {
          console.log('⚠️ Error al crear producto en productos_ar:', err);
         }
         
         showSuccess(`✅ Producto encontrado en ${fuenteAPI}! Completa los datos para tu comercio.`, 'Producto Encontrado');
         setStep('edit');
             } else {
         console.log('❌ Producto no encontrado en API');
         
         // Crear producto vacío
         setFormData({
           comercio_id: COMERCIO_ID,
           codigo_personalizado: codigoABuscar,
           nombre: '',
           marca: '',
          precio_compra: null,
          precio_venta: null,
          categoria: 'General',
          stock_actual: null,
          stock_minimo: null,
           descripcion: '',
           imagen_url: ''
         });
         
        // PASO 2: Crear producto básico en productos_ar
        console.log('💾 Creando producto básico en productos_ar...');
        
         try {
          const productoArBasico = {
             codigo_barras: codigoABuscar,
             nombre: 'Producto sin nombre',
             marca: 'Sin marca',
             categoria: 'General',
             descripcion: 'Producto creado manualmente',
             imagen_url: '',
             precio: 0.00,
             precio_compra: 0.00,
             stock_disponible: 0,
             unidad_medida: 'unidad',
             es_argentino: true
           };
           
          const response = await fetch(`${API_BASE_URL}/productos_ar_crear.php`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(productoArBasico)
          });
          
          const resultado = await response.json();
          if (resultado.success) {
            console.log('✅ Producto básico creado en productos_ar');
           } else {
            console.log('⚠️ Error al crear producto básico en productos_ar:', resultado.message);
           }
         } catch (err) {
          console.log('⚠️ Error al crear producto básico en productos_ar:', err);
         }
         
         showSuccess('🆕 Producto no encontrado. Crea uno nuevo para tu comercio.', 'Nuevo Producto');
         setStep('edit');
       }
    } catch (err) {
      console.error('❌ Error:', err);
      showError('Error al buscar producto: ' + err.message, 'Error de Búsqueda');
    } finally {
      setLoading(false);
    }
  };

     

  // Función simple para guardar producto
  const guardarProducto = async () => {
    if (!formData.nombre.trim()) {
      showError('El nombre es obligatorio', 'Validación');
      return;
    }

    if (formData.precio_compra < 0) {
      showError('El precio de compra no puede ser negativo', 'Validación');
      return;
    }

    if (formData.precio_venta <= 0) {
      showError('El precio de venta debe ser mayor a 0', 'Validación');
      return;
    }

    if (formData.precio_venta <= formData.precio_compra) {
      showError('El precio de venta debe ser mayor al precio de compra', 'Validación');
      return;
    }

    setLoading(true);
    // setError eliminado - ahora se usan popups

    try {
      console.log('💾 Iniciando guardado del producto...');
      console.log('📋 Datos a enviar:', formData);
      
      // Determinar si es una actualización o creación nueva
      // Determinar si es una actualización o creación nueva
      // Si tenemos productoExistente, es una edición
      let esActualizacion = !!productoExistente;
      
      const endpoint = 'productos_comercio_actualizar.php'; // Usar el endpoint correcto para actualizar
      const metodo = 'POST'; // Usar POST para ambos casos
      
      console.log('🔄 Operación:', esActualizacion ? 'ACTUALIZACIÓN' : 'CREACIÓN NUEVA');
      console.log('🌐 Endpoint:', endpoint);
      console.log('📡 Método:', metodo);
      // Obtener token para la API
      const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
      const urlConToken = `${API_BASE_URL}/${endpoint}?token=${token}`;
      console.log('🌐 URL de la API:', urlConToken);
      
      // Convertir imagen blob a base64 si existe
      let imagenBase64 = null;
      if (formData.imagen_url && formData.imagen_url.startsWith('blob:')) {
        try {
          const response = await fetch(formData.imagen_url);
          const blob = await response.blob();
          const reader = new FileReader();
          imagenBase64 = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.error('Error convirtiendo imagen a base64:', error);
        }
      } else if (formData.imagen_url && !formData.imagen_url.startsWith('blob:')) {
        // Si es una imagen existente de la base de datos, mantener la URL original
        console.log('🖼️ [MODAL] Manteniendo imagen existente:', formData.imagen_url);
      }
       
       // Preparar datos del producto incluyendo clasificación dietética principal y packaging
       const datosProducto = {
         ...formData,
         imagen_base64: imagenBase64, // Enviar imagen como base64
         imagen_url: imagenBase64 ? '' : formData.imagen_url, // Limpiar imagen_url si tenemos base64
         clasificacion_principal_id: clasificacionPrincipal ? parseInt(clasificacionPrincipal) : null,
         clasificaciones_dieteticas: clasificacionPrincipal ? JSON.stringify([parseInt(clasificacionPrincipal)]) : null,
         fuente_clasificacion: deteccionAutomatica ? 'API_Automatica' : 'Manual',
         ...packagingData
       };
       
       // Si es una actualización, incluir el ID del producto existente
       if (esActualizacion && productoExistente && productoExistente.id) {
         datosProducto.id = productoExistente.id;
         console.log('🔄 [EDIT] Enviando ID del producto existente:', productoExistente.id);
         console.log('🔄 [EDIT] Producto existente completo:', productoExistente);
       } else {
         console.log('⚠️ [EDIT] No se encontró ID del producto existente');
         console.log('⚠️ [EDIT] esActualizacion:', esActualizacion);
         console.log('⚠️ [EDIT] productoExistente:', productoExistente);
       }
      
             console.log('🌱 Datos del producto con clasificación especial:', datosProducto);
       console.log('🌟 Clasificación especial original:', clasificacionPrincipal);
       console.log('🌟 Tipo original:', typeof clasificacionPrincipal);
       console.log('🌟 Clasificación especial convertida a enviar:', datosProducto.clasificacion_principal_id);
       console.log('🌟 Tipo convertido:', typeof datosProducto.clasificacion_principal_id);
       console.log('🌟 Clasificaciones dietéticas a enviar:', datosProducto.clasificaciones_dieteticas);
       console.log('🌟 Fuente clasificación:', datosProducto.fuente_clasificacion);
      
      const response = await fetch(urlConToken, {
        method: metodo,
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(datosProducto)
      });

      console.log('📡 Respuesta del servidor:');
      console.log('- Status:', response.status);
      console.log('- Status Text:', response.statusText);
      console.log('- Headers:', Object.fromEntries(response.headers.entries()));

      let data;
      const responseText = await response.text();
      console.log('📄 Respuesta en texto:', responseText);
      
      try {
        data = JSON.parse(responseText);
        console.log('✅ Respuesta parseada como JSON:', data);
      } catch (parseError) {
        console.error('❌ Error al parsear JSON:', parseError);
        console.log('📄 Respuesta original:', responseText);
        throw new Error(`Respuesta del servidor no es JSON válido: ${responseText}`);
      }

      if (data.success) {
        console.log('✅ Producto procesado exitosamente:', data.producto);
        console.log('🔍 [DEBUG] esActualizacion:', !!productoExistente);
        console.log('🔍 [DEBUG] clasificacion_principal_id en respuesta:', data.producto.clasificacion_principal_id);
        console.log('🔍 [DEBUG] Respuesta completa del servidor:', JSON.stringify(data, null, 2));
        
        // NOTA: NO modificamos productos_ar desde el comercio
        // La tabla productos_ar es universal y solo se consulta
        
        // Notificar al componente padre que se agregó/actualizó un producto
        if (onProductAdded) {
          console.log('📢 Notificando al componente padre sobre el producto:', data.producto);
          const esActualizacion = !!productoExistente;
          const action = esActualizacion ? 'edit' : 'add';
          onProductAdded(action, data.producto);
        }
        
        // Mostrar popup de éxito
        const esActualizacion = !!productoExistente;
        showSuccess(
          `✅ Producto ${esActualizacion ? 'actualizado' : 'guardado'} exitosamente!`, 
          esActualizacion ? 'Producto Actualizado' : 'Producto Guardado'
        );
        
        console.log('✅ [MODAL] Producto guardado exitosamente, mostrando popup de éxito');
        
        // Limpiar formulario después de guardar exitosamente
        setFormData({
          comercio_id: COMERCIO_ID,
          codigo_personalizado: '',
          nombre: '',
          marca: '',
          precio_compra: '',
          precio_venta: '',
          categoria: 'General',
          stock_actual: '',
          stock_minimo: '',
          descripcion: '',
          imagen_url: ''
        });
        
        // Limpiar imagen y packaging
        setImagePreview('');
        setSelectedFile(null);
        setPackagingData({
          tipo_packaging_id: null,
          cantidad_packaging: null,
          packaging_completo: null
        });
        
        // Después de guardar exitosamente, volver al paso de escaneo para permitir agregar otro producto
        console.log('🔄 [GUARDAR] Volviendo al paso de escaneo para agregar otro producto');
        setStep('scan');
        setCodigoBarras('');
        
        // Enfocar el campo de código para el siguiente producto
        setTimeout(() => {
          if (codigoInputRef.current) {
            codigoInputRef.current.focus();
          }
        }, 100);
      } else {
        const errorMessage = data.message || data.error || 'Error desconocido al guardar';
        showError(`Error al ${esActualizacion ? 'actualizar' : 'guardar'}: ${errorMessage}`, 'Error del Servidor');
        console.error('❌ Error del servidor:', data);
      }
    } catch (err) {
      console.error('❌ Error completo al guardar:', err);
      console.error('❌ Stack trace:', err.stack);
      
      const esActualizacion = !!productoExistente;
      let errorMessage = `Error al ${esActualizacion ? 'actualizar' : 'guardar'}: `;
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMessage += 'Error de conexión con el servidor. Verifica que el backend esté ejecutándose.';
      } else if (err.name === 'SyntaxError') {
        errorMessage += 'Error en la respuesta del servidor. La respuesta no es válida.';
      } else {
        errorMessage += err.message || 'Error desconocido';
      }
      
      showError(errorMessage, 'Error de Conexión');
    } finally {
      setLoading(false);
    }
  };

  // Función para iniciar scanner automático
  const iniciarScanner = async () => {
    try {
      setScanning(true);
      setShowCamera(true);
      // setError eliminado - ahora se usan popups
      
      console.log('🔍 Iniciando scanner automático...');
      
      // Verificar si getUserMedia está disponible
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia no está disponible en este navegador');
      }
      
      // Inicializar scanner de códigos de barras
      const { BrowserMultiFormatReader } = await import('@zxing/library');
      scannerRef.current = new BrowserMultiFormatReader();
      
      // Intentar diferentes configuraciones de video
      let stream = null;
      const videoConfigs = [
        // Configuración preferida
        { 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        },
        // Configuración alternativa 1
        { 
          video: { 
            facingMode: 'environment',
            width: { ideal: 640 },
            height: { ideal: 480 }
          } 
        },
        // Configuración alternativa 2 (sin facingMode)
        { 
          video: { 
            width: { ideal: 640 },
            height: { ideal: 480 }
          } 
        },
        // Configuración básica
        { video: true }
      ];
      
      // Probar cada configuración hasta que una funcione
      for (let i = 0; i < videoConfigs.length; i++) {
        try {
          console.log(`🎥 Probando configuración de video ${i + 1}...`);
          stream = await navigator.mediaDevices.getUserMedia(videoConfigs[i]);
          console.log(`✅ Configuración ${i + 1} funcionó`);
          break;
        } catch (configError) {
          console.log(`❌ Configuración ${i + 1} falló:`, configError.message);
          if (i === videoConfigs.length - 1) {
            throw configError; // Si todas fallan, lanzar el último error
          }
        }
      }
      
      if (!stream) {
        throw new Error('No se pudo acceder a la cámara con ninguna configuración');
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        console.log('✅ Video configurado correctamente');
        
        // Esperar a que el video esté listo
        await new Promise((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = resolve;
          }
        });
      }
      
      // Iniciar detección automática
      console.log('🎯 Iniciando detección de códigos...');
      
      // Configurar el scanner con más opciones
      scannerRef.current.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (result, error) => {
          if (result) {
            console.log('🎯 Código detectado automáticamente:', result.text);
            
            // Cerrar cámara inmediatamente
            cerrarCamara();
            
            // Establecer el código de barras
            setCodigoBarras(result.text);
            
            // Buscar producto automáticamente sin delay
            console.log('🔍 Buscando producto automáticamente...');
            buscarProducto(result.text);
          }
          if (error && error.name !== 'NotFoundException') {
            console.log('⚠️ Error de scanner:', error);
          }
        }
      );
      
      console.log('✅ Scanner iniciado con detección automática');
    } catch (err) {
      console.error('❌ Error al iniciar scanner:', err);
      
      let errorMessage = 'Error al iniciar scanner: ';
      if (err.name === 'NotAllowedError') {
        errorMessage += 'Permisos de cámara denegados. Por favor, permite el acceso a la cámara y recarga la página.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'No se encontró ninguna cámara. Verifica que tengas una cámara conectada.';
      } else if (err.name === 'NotReadableError') {
        errorMessage += 'La cámara está siendo usada por otra aplicación. Cierra otras aplicaciones que usen la cámara.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage += 'La configuración de la cámara no es compatible. Intenta con un navegador diferente.';
      } else {
        errorMessage += err.message;
      }
      
      showError(errorMessage, 'Error del Scanner');
      setScanning(false);
      setShowCamera(false);
    }
  };

  // Función simple para cámara manual
  const abrirCamara = async () => {
    try {
      setShowCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      showError('Error al abrir cámara: ' + err.message, 'Error de Cámara');
    }
  };

  const cerrarCamara = () => {
    console.log('🔒 Cerrando cámara y scanner...');
    
    // Detener scanner si está activo
    if (scannerRef.current) {
      try {
        scannerRef.current.reset();
        console.log('✅ Scanner detenido');
      } catch (err) {
        console.log('⚠️ Error al detener scanner:', err);
      }
    }
    
    // Detener stream de video
    if (videoRef.current?.srcObject) {
      try {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => {
          track.stop();
          console.log('✅ Track detenido:', track.kind);
        });
        videoRef.current.srcObject = null;
      } catch (err) {
        console.log('⚠️ Error al detener video:', err);
      }
    }
    
    // Resetear estados
    setScanning(false);
    setShowCamera(false);
    
    console.log('✅ Cámara cerrada completamente');
  };

  const tomarFoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);
      
      canvas.toBlob((blob) => {
        // Validar la imagen capturada
        validateImageBlob(blob, canvas.width, canvas.height);
        cerrarCamara();
      }, 'image/jpeg', 0.8);
    }
  };

  // Función para validar imagen capturada
  const validateImageBlob = (blob, width, height) => {
    // Validar dimensiones mínimas
    if (width < 100 || height < 100) {
      showError('❌ La imagen capturada es demasiado pequeña (mínimo 100x100px)');
      return;
    }
    
    // Validar dimensiones máximas
    if (width > 4000 || height > 4000) {
      showError('❌ La imagen capturada es demasiado grande (máximo 4000x4000px)');
      return;
    }
    
    // Validar peso del blob
    if (blob.size < 1024) {
      showError('❌ La imagen capturada es demasiado pequeña (mínimo 1KB)');
      return;
    }
    
    if (blob.size > 5 * 1024 * 1024) {
      showError('❌ La imagen capturada es demasiado grande (máximo 5MB)');
      return;
    }
    
    // Validar proporción
    const ratio = Math.max(width / height, height / width);
    if (ratio > 10) {
      showError('❌ La imagen capturada tiene proporciones extremas (máximo 10:1)');
      return;
    }
    
    // Si todo está bien, procesar la imagen
    const url = URL.createObjectURL(blob);
    setFormData(prev => ({ ...prev, imagen_url: url }));
    setImagePreview(url);
    setSelectedFile(blob);
    setImageError('');
    
    // Mostrar información de la imagen
    const sizeKB = Math.round(blob.size / 1024);
    const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
    const sizeText = sizeKB < 1024 ? `${sizeKB} KB` : `${sizeMB} MB`;
    
    showSuccess(`Foto capturada: ${width}x${height}px, ${sizeText}`, 'Foto Capturada');
  };

  // Función para manejar selección de archivo
  const handleImageFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar archivo básico
      if (!file.type.startsWith('image/')) {
        showError('Solo se permiten archivos de imagen (JPG, PNG, GIF, WebP)', 'Error de Imagen');
        return;
      }

      // Validar peso del archivo (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('El archivo es demasiado grande (máximo 5MB)', 'Error de Imagen');
        return;
      }

      // Validar peso mínimo (mínimo 1KB)
      if (file.size < 1024) {
        showError('El archivo es demasiado pequeño (mínimo 1KB)', 'Error de Imagen');
        return;
      }

      // Crear una imagen para validar dimensiones
      const img = new Image();
      img.onload = () => {
        // Validar dimensiones (máximo 4000x4000px, mínimo 100x100px)
        if (img.width > 4000 || img.height > 4000) {
          showError('La imagen es demasiado grande (máximo 4000x4000px)', 'Error de Imagen');
          return;
        }
        
        if (img.width < 100 || img.height < 100) {
          showError('La imagen es demasiado pequeña (mínimo 100x100px)', 'Error de Imagen');
          return;
        }

        // Validar proporción (máximo 10:1 o 1:10)
        const ratio = Math.max(img.width / img.height, img.height / img.width);
        if (ratio > 10) {
          showError('La imagen tiene proporciones extremas (máximo 10:1)', 'Error de Imagen');
          return;
        }

        // Si todo está bien, procesar la imagen
        processImageFile(file, img.width, img.height);
      };

      img.onerror = () => {
        showError('Error al cargar la imagen. Verifica que sea un archivo válido.', 'Error de Imagen');
      };

      // Cargar la imagen para validar
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  // Función para procesar la imagen después de validar
  const processImageFile = async (file, width, height) => {
    try {
      // Guardar archivo seleccionado
      setSelectedFile(file);
      
      // Crear preview temporal para mostrar al usuario
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Subir imagen al servidor
      const formData = new FormData();
      formData.append('imagen', file);
      formData.append('comercio_id', COMERCIO_ID);

      const response = await fetch(`${API_BASE_URL}/upload_imagen.php`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Guardar la URL de la imagen subida en lugar de base64
        setFormData(prev => ({ ...prev, imagen_url: data.imagen_url }));
        
        // Mostrar información de la imagen
        const sizeKB = Math.round(file.size / 1024);
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        const sizeText = sizeKB < 1024 ? `${sizeKB} KB` : `${sizeMB} MB`;
        
        showSuccess(`Imagen subida: ${file.name} (${width}x${height}px, ${sizeText})`, 'Imagen Cargada');
      } else {
        throw new Error(data.error || 'Error al subir imagen');
      }

     } catch (err) {
       console.error('🔍 [ERROR] Error procesando imagen:', err);
       showError(`Error al procesar imagen: ${err.message}`, 'Error de Imagen');
     }
  };

  // Función para eliminar imagen
  const removeImage = () => {
    setImagePreview('');
    setSelectedFile(null);
    setFormData(prev => ({ ...prev, imagen_url: '' }));
    showSuccess('Imagen eliminada correctamente', 'Imagen Eliminada');
  };

     // Función para guardar/actualizar en productos_ar (catálogo nacional)
   // NOTA: NO modificamos productos_ar desde el comercio
   // La tabla productos_ar es universal y solo se consulta

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => {
      // Solo cerrar si se hace clic en el overlay, no en el contenido
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>{productoExistente ? '✏️ Editar Producto' : '🆕 Alta Nuevo Producto'}</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        

        {/* Contenido */}
        <div className="modal-body">
          {step === 'scan' && (
            <div className="scan-section">
              <h3>📱 Escanear Código de Barras</h3>
              
              {/* Input de búsqueda manual */}
              <div className="input-group">
                <input
                  ref={codigoInputRef}
                  type="text"
                  placeholder="Ingresa el código de barras manualmente"
                  value={codigoBarras}
                  onChange={(e) => setCodigoBarras(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && buscarProducto()}
                  className="form-input"
                />
                <button
                  onClick={() => buscarProducto()}
                  disabled={loading || !codigoBarras.trim()}
                  className="btn btn-primary"
                >
                  {loading ? 'Buscando...' : <><Search size={18} /> Buscar</>}
                </button>
              </div>

              {/* Opciones de captura */}
              <div className="capture-options">
                <div className="capture-buttons">
                  <button 
                    onClick={iniciarScanner} 
                    disabled={scanning}
                    className="btn btn-success"
                  >
                    {scanning ? 'Escaneando...' : <><Camera size={18} /> Scanner Automático</>}
                  </button>
                </div>
                
                
                
                {scanning && (
                  <div className="scanning-info">
                    <p>📱 Apunta la cámara al código de barras. Se detectará automáticamente.</p>
                    <div className="scanning-indicator">
                      <div className="scanning-dot"></div>
                      <div className="scanning-dot"></div>
                      <div className="scanning-dot"></div>
                    </div>
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                      <strong>Estado:</strong> Escaneando activamente...
                    </p>
                  </div>
                )}
                
                
              </div>

              {/* Mensajes eliminados - ahora se usan popups */}
            </div>
          )}

          {step === 'edit' && (
            <div className="edit-section">
              
                             
              
                             {/* Formulario */}
               <div className="form-grid">
                 {/* Primera fila: Código de Barras, Nombre del Producto, Marca */}
                 <div className="form-group">
                   <label>Código de Barras *</label>
                   <input
                     type="text"
                     value={formData.codigo_personalizado}
                     onChange={(e) => setFormData(prev => ({ ...prev, codigo_personalizado: e.target.value }))}
                     className="form-input"
                     disabled
                   />
                 </div>

                 <div className="form-group">
                   <label>Nombre del Producto *</label>
                   <input
                     type="text"
                     value={formData.nombre}
                     onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                     onFocus={() => handleFieldFocus('nombre')}
                     className="form-input"
                     placeholder="Nombre del producto"
                   />
                 </div>

                 <div className="form-group">
                   <label>Marca</label>
                   <input
                     type="text"
                     value={formData.marca}
                     onChange={(e) => setFormData(prev => ({ ...prev, marca: e.target.value }))}
                     onFocus={() => handleFieldFocus('marca')}
                     className="form-input-small"
                     placeholder="Marca del producto"
                   />
                 </div>

                 {/* Segunda fila: Descripción del Producto, Categoría, Presentación, Precio de Compra, Precio de Venta, Stock Inicial, Cantidad en Stock, Stock Mínimo */}
                 <div className="form-group">
                   <label>Descripción del Producto</label>
                   <textarea
                     value={formData.descripcion}
                     onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                     onFocus={() => handleFieldFocus('descripcion')}
                     className="form-input-small"
                     placeholder="Descripción del producto"
                     rows="1"
                   />
                 </div>

                 <div className="form-group">
                   <label>Categoría</label>
                   <select
                     value={formData.categoria}
                     onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                     className="form-input-small"
                   >
                     <option value="General">General</option>
                     <option value="Alimentos">Alimentos</option>
                     <option value="Bebidas">Bebidas</option>
                     <option value="Limpieza">Limpieza</option>
                     <option value="Higiene">Higiene</option>
                     <option value="Frutas y Verduras">Frutas y Verduras</option>
                     <option value="Carnes">Carnes</option>
                     <option value="Lácteos">Lácteos</option>
                     <option value="Panadería">Panadería</option>
                     <option value="Congelados">Congelados</option>
                     <option value="Cereales">Cereales</option>
                     <option value="Snacks">Snacks</option>
                     <option value="Dulces">Dulces</option>
                     <option value="Medicamentos">Medicamentos</option>
                     <option value="Cuidado Personal">Cuidado Personal</option>
                     <option value="Hogar">Hogar</option>
                     <option value="Jardín">Jardín</option>
                     <option value="Mascotas">Mascotas</option>
                     <option value="Tecnología">Tecnología</option>
                     <option value="Ropa">Ropa</option>
                     <option value="Deportes">Deportes</option>
                     <option value="Libros">Libros</option>
                     <option value="Juguetes">Juguetes</option>
                     <option value="Otros">Otros</option>
                   </select>
                 </div>

                 <div className="form-group">
                   <label>📦 Presentación</label>
                   {console.log('📦 [RENDER] packagingData.tipo_packaging_id:', packagingData.tipo_packaging_id)}
                   <select
                     value={packagingData.tipo_packaging_id || ''}
                     onChange={(e) => {
                       const tipoId = e.target.value ? parseInt(e.target.value) : null;
                       let cantidad = packagingData.cantidad_packaging;
                       
                       // Si se selecciona "Unidad", establecer cantidad automáticamente en 1
                       if (tipoId === 1) {
                         cantidad = 1;
                       }
                       
                       setPackagingData(prev => ({ 
                         ...prev, 
                         tipo_packaging_id: tipoId,
                         cantidad_packaging: cantidad
                       }));
                     }}
                     className="form-input-small"
                   >
                     <option value="">Seleccionar</option>
                     <option value="1">Unidad</option>
                     <option value="2">Kilo</option>
                     <option value="3">Gramos</option>
                     <option value="4">Litro</option>
                     <option value="5">Caja</option>
                     <option value="6">Docena</option>
                     <option value="7">Metro</option>
                     <option value="8">Bolsa</option>
                   </select>
                   <div style={{ marginTop: '8px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                     {packagingData.packaging_completo && `Descripción: ${packagingData.packaging_completo}`}
                   </div>
                 </div>

                 <div className="form-group">
                   <label>Precio de Compra</label>
                   <div className="price-input-container">
                     <span className="price-symbol">$</span>
                       <input
                         type="number"
                         step="0.01"
                         min="0"
                         value={formData.precio_compra || ''}
                         onChange={(e) => {
                           const valor = parseFloat(e.target.value) || 0;
                           setFormData(prev => ({ ...prev, precio_compra: valor }));
                         }}
                        onFocus={() => handleFieldFocus('precio_compra')}
                         className="form-input-price-with-symbol"
                         placeholder="0.00"
                       />
                   </div>
                 </div>

                 <div className="form-group">
                   <label>Precio de Venta *</label>
                   <div className="price-input-container">
                     <span className="price-symbol">$</span>
                     <input
                       type="number"
                       step="0.01"
                       min="0"
                       value={formData.precio_venta || ''}
                       onChange={(e) => {
                         const valor = parseFloat(e.target.value) || 0;
                         setFormData(prev => ({ ...prev, precio_venta: valor }));
                       }}
                       onFocus={() => handleFieldFocus('precio_venta')}
                       className="form-input-price-with-symbol"
                       placeholder="0.00"
                     />
                   </div>
                 </div>

                 <div className="form-group">
                   <label>Stock Actual *</label>
                   <input
                     type="number"
                     min="0"
                     value={formData.stock_actual || ''}
                     onChange={(e) => setFormData(prev => ({ ...prev, stock_actual: parseInt(e.target.value) || 0 }))}
                     onFocus={() => handleFieldFocus('stock_actual')}
                     className="form-input-small"
                     placeholder="0"
                   />
                 </div>


                 <div className="form-group">
                   <label>Stock Mínimo</label>
                   <input
                     type="number"
                     min="0"
                     value={formData.stock_minimo || ''}
                     onChange={(e) => setFormData(prev => ({ ...prev, stock_minimo: parseInt(e.target.value) || 0 }))}
                     onFocus={() => handleFieldFocus('stock_minimo')}
                     className="form-input-small"
                     placeholder="0"
                   />
                 </div>



       {/* Descripción personalizada del packaging */}
       {packagingData.tipo_packaging_id && (
         <div className="form-group packaging-description">
           <label>Descripción de Presentación</label>
           <textarea
             value={packagingData.packaging_completo || ''}
             onChange={(e) => setPackagingData(prev => ({ ...prev, packaging_completo: e.target.value }))}
             placeholder="Descripción personalizada (ej: Botella de 1.5 litros, Caja de 500 gramos, etc.)"
             rows="2"
             className="form-input-large"
           />
           
           {/* Vista previa del packaging */}
           {packagingData.cantidad_packaging && (
             <div className="packaging-preview">
               <span>
                 📦 Presentación: {packagingData.cantidad_packaging} {getNombreTipoPackaging(packagingData.tipo_packaging_id)}
               </span>
             </div>
           )}
         </div>
       )}

               </div>

              {/* Mensajes eliminados - ahora se usan popups */}

              {/* Botones - AHORA AL FINAL del popup como solicitaste */}
              <div className="modal-actions">
                
                {/* Botón para crear producto nuevo (resetear todo) */}
                <button 
                  onClick={() => {
                    setStep('scan');
                    setCodigoBarras('');
                    // setError eliminado - ahora se usan popups
                    // setSuccess eliminado - ahora se usan popups
                    setShowCamera(false);
                    setScanning(false);
                    setClasificacionPrincipal(null);
                    setDeteccionAutomatica(null);
                    setPackagingData({
                      tipo_packaging_id: null,
                      cantidad_packaging: null,
                      packaging_completo: null
                    });
                    setFormData({
                      comercio_id: COMERCIO_ID,
                      codigo_personalizado: '',
                      nombre: '',
                      marca: '',
          precio_compra: null,
          precio_venta: null,
          categoria: 'General',
          stock_actual: null,
          stock_minimo: null,
                      descripcion: '',
                      imagen_url: ''
                    });
                  }}
                  className="btn btn-warning"
                  style={{ marginLeft: '10px' }}
                >
                  🆕 Crear Producto Nuevo
                </button>
                
                <button onClick={guardarProducto} disabled={loading} className="btn btn-success">
                  {loading ? 'Procesando...' : (
                    <>
                      <Save size={18} /> 
                      {formData.codigo_personalizado && formData.nombre ? 'Actualizar Producto' : 'Guardar Producto'}
                    </>
                  )}
                </button>
                
              </div>
            </div>
          )}

          {step === 'form' && (
            <div className="edit-section">
              
              {/* Formulario */}
              <div className="form-grid">
                {/* Primera fila: Código de Barras, Nombre del Producto, Marca */}
                <div className="form-group">
                  <label>Código de Barras *</label>
                  <input
                    type="text"
                    value={formData.codigo_personalizado}
                    onChange={(e) => setFormData(prev => ({ ...prev, codigo_personalizado: e.target.value }))}
                    className="form-input"
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label>Nombre del Producto *</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                    onFocus={() => handleFieldFocus('nombre')}
                    className="form-input"
                    placeholder="Nombre del producto"
                  />
                </div>

                <div className="form-group">
                  <label>Marca</label>
                  <input
                    type="text"
                    value={formData.marca}
                    onChange={(e) => setFormData(prev => ({ ...prev, marca: e.target.value }))}
                    onFocus={() => handleFieldFocus('marca')}
                    className="form-input-small"
                    placeholder="Marca del producto"
                  />
                </div>

                {/* Segunda fila: Descripción del Producto, Categoría, Presentación, Precio de Compra, Precio de Venta, Stock Inicial, Cantidad en Stock, Stock Mínimo */}
                <div className="form-group">
                  <label>Descripción del Producto</label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                    onFocus={() => handleFieldFocus('descripcion')}
                    className="form-input-small"
                    placeholder="Descripción del producto"
                    rows="1"
                  />
                </div>

                <div className="form-group">
                  <label>Categoría</label>
                  <select
                    value={formData.categoria}
                    onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                    className="form-input-small"
                  >
                    <option value="General">General</option>
                    <option value="Alimentos">Alimentos</option>
                    <option value="Bebidas">Bebidas</option>
                    <option value="Limpieza">Limpieza</option>
                    <option value="Higiene">Higiene</option>
                    <option value="Frutas y Verduras">Frutas y Verduras</option>
                    <option value="Carnes">Carnes</option>
                    <option value="Lácteos">Lácteos</option>
                    <option value="Panadería">Panadería</option>
                    <option value="Congelados">Congelados</option>
                    <option value="Cereales">Cereales</option>
                    <option value="Snacks">Snacks</option>
                    <option value="Dulces">Dulces</option>
                    <option value="Medicamentos">Medicamentos</option>
                    <option value="Cuidado Personal">Cuidado Personal</option>
                    <option value="Hogar">Hogar</option>
                    <option value="Jardín">Jardín</option>
                    <option value="Mascotas">Mascotas</option>
                    <option value="Tecnología">Tecnología</option>
                    <option value="Ropa">Ropa</option>
                    <option value="Deportes">Deportes</option>
                    <option value="Libros">Libros</option>
                    <option value="Juguetes">Juguetes</option>
                    <option value="Otros">Otros</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>📦 Presentación</label>
                  <select
                    value={packagingData.tipo_packaging_id || ''}
                    onChange={(e) => {
                      const tipoId = e.target.value ? parseInt(e.target.value) : null;
                      let cantidad = packagingData.cantidad_packaging;
                      
                      // Si se selecciona "Unidad", establecer cantidad automáticamente en 1
                      if (tipoId === 1) {
                        cantidad = 1;
                      }
                      
                      setPackagingData(prev => ({ 
                        ...prev, 
                        tipo_packaging_id: tipoId,
                        cantidad_packaging: cantidad
                      }));
                    }}
                    className="form-input-small"
                  >
                    <option value="">Seleccionar</option>
                    <option value="1">Unidad</option>
                    <option value="2">Kilo</option>
                    <option value="3">Gramos</option>
                    <option value="4">Litro</option>
                    <option value="5">Caja</option>
                    <option value="6">Docena</option>
                    <option value="7">Metro</option>
                    <option value="8">Bolsa</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Precio de Compra</label>
                  <div className="price-input-container">
                    <span className="price-symbol">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.precio_compra}
                        onChange={(e) => {
                          const valor = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({ ...prev, precio_compra: valor }));
                        }}
                        onFocus={() => handleFieldFocus('precio_compra')}
                        className="form-input-price-with-symbol"
                        placeholder="0.00"
                      />
                  </div>
                </div>

                <div className="form-group">
                  <label>Precio de Venta *</label>
                  <div className="price-input-container">
                    <span className="price-symbol">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.precio_venta}
                      onChange={(e) => {
                        const valor = parseFloat(e.target.value) || 0;
                        setFormData(prev => ({ ...prev, precio_venta: valor }));
                      }}
                      onFocus={() => handleFieldFocus('precio_venta')}
                      className="form-input-price-with-symbol"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Stock Actual *</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock_actual}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock_actual: parseInt(e.target.value) || 0 }))}
                    onFocus={() => handleFieldFocus('stock_actual')}
                    className="form-input-small"
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Stock Mínimo</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock_minimo || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock_minimo: parseInt(e.target.value) || 0 }))}
                    onFocus={() => handleFieldFocus('stock_minimo')}
                    className="form-input-small"
                    placeholder="0"
                  />
                </div>

                {/* Tercera fila: Imagen del Producto - Centrada */}
                <div className="form-group image-section">
                  <label>🖼️ Imagen del Producto</label>
                  
                   <div className="image-preview-container">
                     {imagePreview && imagePreview.trim() !== '' ? (
                       <div className="image-preview">
                         <img 
                           src={imagePreview} 
                           alt="Vista previa" 
                           onLoad={() => console.log('✅ Imagen cargada correctamente:', imagePreview)}
                           onError={(e) => {
                             console.log('❌ Error cargando imagen:', imagePreview, e);
                             // Si hay error, limpiar la imagen
                             setImagePreview('');
                             setFormData(prev => ({ ...prev, imagen_url: '' }));
                           }}
                         />
                         <button 
                           type="button" 
                           onClick={removeImage}
                           className="remove-image-btn"
                           title="Eliminar imagen"
                         >
                           <X />
                         </button>
                       </div>
                     ) : (
                       <div className="image-placeholder">
                         <Camera />
                         <p>📷 Sin imagen</p>
                         <div className="image-controls">
                           <label className="btn btn-secondary cursor-pointer">
                             <Upload size={20} />
                             Subir Imagen
                             <input
                               type="file"
                               accept="image/*"
                               onChange={handleImageFileSelect}
                               style={{ display: 'none' }}
                               disabled={loading}
                             />
                           </label>
                           <button
                             type="button"
                             onClick={abrirCamara}
                             disabled={loading}
                             className="btn btn-primary"
                             title="Capturar foto con cámara"
                           >
                             <Camera size={20} />
                             Capturar Foto
                           </button>
                         </div>
                       </div>
                     )}
                   </div>

                   {/* Información de validación */}
                   <div className="file-info">
                     <div>📁 Tipos: JPG, PNG, GIF, WebP • 📏 Dimensiones: 100x100px - 4000x4000px</div>
                     <div>⚖️ Peso: 1KB - 5MB • 📐 Proporción máxima: 10:1</div>
                     {selectedFile && (
                       <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px', fontSize: '12px' }}>
                         <strong>📄 Archivo seleccionado:</strong> {selectedFile.name}
                       </div>
                     )}
                     {imagePreview && !selectedFile && formData.imagen_url && (
                       <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0fff0', borderRadius: '4px', fontSize: '12px' }}>
                         <strong>🖼️ Imagen actual:</strong> {formData.imagen_url.split('/').pop() || 'Imagen del producto'}
                       </div>
                     )}
                   </div>

                   {/* Controles de imagen cuando hay imagen */}
                   {imagePreview && imagePreview.trim() !== '' && (
                     <div className="image-controls">
                       <div className="actions-container-left">
                         <label className="btn btn-secondary cursor-pointer">
                           <Camera />
                           Cambiar Imagen
                           <input
                             type="file"
                             accept="image/*"
                             onChange={handleImageFileSelect}
                             style={{ display: 'none' }}
                             disabled={loading}
                           />
                         </label>
                         
                         <button
                           type="button"
                           onClick={abrirCamara}
                           disabled={loading}
                           className="btn btn-primary"
                           title="Capturar nueva foto"
                         >
                           <Camera />
                           Nueva Foto
                         </button>
                       </div>
                     </div>
                   )}
                  
                  {imageError && (
                    <div className="image-error">
                      {imageError}
                    </div>
                  )}
                </div>
              </div>

              {/* Sección de Clasificación Dietética */}
              <div className="form-section">
                <h4>🌱 Clasificación Dietética</h4>
                <ClasificacionesDieteticas
                  clasificacionPrincipal={clasificacionPrincipal}
                  onClasificacionPrincipalChange={setClasificacionPrincipal}
                  deteccionAutomatica={deteccionAutomatica}
                  disabled={loading}
                />
                {console.log('🎯 [DEBUG] ClasificacionesDieteticas recibiendo clasificacionPrincipal:', clasificacionPrincipal)}
              </div>

              {/* Botones de acción */}
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setStep('scan')}
                  className="btn btn-secondary"
                >
                  ← Volver a Escanear
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    // Limpiar formulario para nuevo producto
                    setFormData({
                      comercio_id: COMERCIO_ID,
                      codigo_personalizado: '',
                      nombre: '',
                      marca: '',
          precio_compra: null,
          precio_venta: null,
          categoria: 'General',
          stock_actual: null,
          stock_minimo: null,
                      descripcion: '',
                      imagen_url: ''
                    });
                    setImagePreview(null);
                    setStep('scan');
                    setCodigoBarras('');
                    // Enfocar el campo de código
                    setTimeout(() => {
                      if (codigoInputRef.current) {
                        codigoInputRef.current.focus();
                      }
                    }, 100);
                  }}
                  className="btn btn-success"
                >
                  ➕ Agregar Otro
                </button>
                <button 
                  type="button" 
                  onClick={guardarProducto}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : '💾 Guardar Producto'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Popup de cámara */}
        {showCamera && (
          <div className="camera-popup">
            <div className="camera-header">
              <h3>📸 {scanning ? 'Scanner Automático' : 'Cámara Manual'}</h3>
              <button onClick={cerrarCamara} className="camera-close">✕</button>
            </div>
            
            <video ref={videoRef} autoPlay playsInline muted className="camera-video" />
            
            <div className="camera-controls">
              {!scanning && (
                <button onClick={tomarFoto} className="camera-capture">
                  <Camera size={24} /> Tomar Foto
                </button>
              )}
              <button onClick={cerrarCamara} className="btn btn-secondary">
                Cerrar
              </button>
            </div>
            
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        )}

        {/* Toast de notificaciones */}
        <SimpleToast
          isOpen={toast.isOpen}
          onClose={hideToast}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          showCloseButton={toast.showCloseButton}
          autoClose={toast.autoClose}
          autoCloseDelay={toast.autoCloseDelay}
        />
        
      </div>
    </div>
  );
};

export default ProductoModal;
